import React from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { FaSearch } from 'react-icons/fa';
import { customStyles } from '~/Styles/reactSelectStyles';

// W-0595 — filtros CUMULATIVOS (todos opcionais exceto GE). Lookups em RelNegocioOpcaoItem
// => { codigo, descricao }. O backend faz AND de cada filtro preenchido (não é single-select).
const PLACEHOLDER_SELECIONE = 'Selecione...';

// Moeda Mercadoria/Pagamento espelham os TRxDBLookupCombo do Delphi (URelNeg.pas:99-100):
// lookup incremental por digitação, NÃO dropdown que pinta a lista inteira. A tabela Ativo
// tem dezenas de milhares de linhas — pintar tudo de uma vez trava a tela (mesma classe do
// W-0488). Por isso os Ativos vão num AsyncSelect com filtro client-side + teto de resultados.
const MIN_BUSCA_ATIVO = 2; // só monta o menu após digitar (paridade type-to-search Delphi)
const MAX_OPCOES_ATIVO = 50; // teto de itens pintados por busca (evita render de milhares)

// Converte lista de opções (codigo/descricao) em opções react-select (value/label).
const paraOpcoes = lista =>
  (lista || []).map(o => ({ value: o.codigo, label: o.descricao }));

const FormFiltros = ({
  filtros,
  ges,
  contas,
  ativos,
  usuarios,
  carregandoContas,
  onChange,
  onGerar,
  carregando,
}) => {
  const opcoesGe = paraOpcoes(ges);
  const opcoesConta = paraOpcoes(contas);
  const opcoesAtivo = paraOpcoes(ativos);
  const opcoesUsuario = paraOpcoes(usuarios);

  const valorSelecionado = (opcoes, codigo) =>
    codigo === null || codigo === undefined || codigo === ''
      ? null
      : opcoes.find(o => o.value === Number(codigo)) || null;

  // AsyncSelect dos Ativos: filtra a lista já em memória (opcoesAtivo) por nome e limita o
  // número de itens devolvidos. Só busca a partir de MIN_BUSCA_ATIVO chars, então o menu
  // nunca tenta pintar a tabela inteira (causa do freeze). Roda síncrono via Promise.resolve.
  const carregarAtivos = entrada => {
    const termo = (entrada || '').trim().toLowerCase();
    if (termo.length < MIN_BUSCA_ATIVO) return Promise.resolve([]);
    const filtradas = opcoesAtivo
      .filter(o => o.label && o.label.toLowerCase().indexOf(termo) !== -1)
      .slice(0, MAX_OPCOES_ATIVO);
    return Promise.resolve(filtradas);
  };

  const handleSelect = (campo, opt) => {
    onChange({ ...filtros, [campo]: opt ? opt.value : null });
  };

  // Ao trocar de GE, reseta a conta (a lista de contas depende do GE).
  const handleGeChange = opt => {
    onChange({
      ...filtros,
      codigo_grupo_empresa: opt ? opt.value : null,
      codigo_conta: null,
    });
  };

  const handleInput = (campo, valor) => {
    onChange({ ...filtros, [campo]: valor });
  };

  return (
    <>
      <Row className="align-items-end">
        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>
              Grupo de Empresa (GE) <span className="text-danger">*</span>
            </Form.Label>
            <Select
              options={opcoesGe}
              value={valorSelecionado(opcoesGe, filtros.codigo_grupo_empresa)}
              onChange={opt => handleGeChange(opt)}
              styles={customStyles}
              placeholder={PLACEHOLDER_SELECIONE}
              isClearable
              isDisabled={carregando}
              noOptionsMessage={() => 'Nenhum GE disponível'}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>Cliente (Conta)</Form.Label>
            <Select
              options={opcoesConta}
              value={valorSelecionado(opcoesConta, filtros.codigo_conta)}
              onChange={opt => handleSelect('codigo_conta', opt)}
              styles={customStyles}
              placeholder={
                filtros.codigo_grupo_empresa
                  ? PLACEHOLDER_SELECIONE
                  : 'Selecione o GE primeiro'
              }
              isClearable
              isDisabled={carregando || !filtros.codigo_grupo_empresa}
              isLoading={carregandoContas}
              noOptionsMessage={() => 'Nenhuma conta encontrada'}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>Usuário</Form.Label>
            <Select
              options={opcoesUsuario}
              value={valorSelecionado(opcoesUsuario, filtros.codigo_usuario)}
              onChange={opt => handleSelect('codigo_usuario', opt)}
              styles={customStyles}
              placeholder={PLACEHOLDER_SELECIONE}
              isClearable
              isDisabled={carregando}
              noOptionsMessage={() => 'Nenhum usuário encontrado'}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="align-items-end mt-2">
        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label>Moeda Mercadoria (Ativo)</Form.Label>
            <AsyncSelect
              cacheOptions
              defaultOptions={false}
              loadOptions={carregarAtivos}
              value={valorSelecionado(
                opcoesAtivo,
                filtros.codigo_ativo_mercadoria
              )}
              onChange={opt => handleSelect('codigo_ativo_mercadoria', opt)}
              styles={customStyles}
              placeholder="Digite para buscar o ativo..."
              isClearable
              isDisabled={carregando}
              loadingMessage={() => 'Buscando...'}
              noOptionsMessage={({ inputValue }) =>
                inputValue && inputValue.trim().length >= MIN_BUSCA_ATIVO
                  ? 'Nenhum ativo encontrado'
                  : 'Digite ao menos 2 letras'
              }
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label>Moeda Pagamento (Ativo)</Form.Label>
            <AsyncSelect
              cacheOptions
              defaultOptions={false}
              loadOptions={carregarAtivos}
              value={valorSelecionado(
                opcoesAtivo,
                filtros.codigo_ativo_pagamento
              )}
              onChange={opt => handleSelect('codigo_ativo_pagamento', opt)}
              styles={customStyles}
              placeholder="Digite para buscar o ativo..."
              isClearable
              isDisabled={carregando}
              loadingMessage={() => 'Buscando...'}
              noOptionsMessage={({ inputValue }) =>
                inputValue && inputValue.trim().length >= MIN_BUSCA_ATIVO
                  ? 'Nenhum ativo encontrado'
                  : 'Digite ao menos 2 letras'
              }
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="align-items-end mt-2">
        <Col xs={6} md={3}>
          <Form.Group>
            <Form.Label>Data Inicial</Form.Label>
            <Form.Control
              type="date"
              value={filtros.data_ini || ''}
              onChange={e => handleInput('data_ini', e.target.value)}
              disabled={carregando}
            />
          </Form.Group>
        </Col>

        <Col xs={6} md={3}>
          <Form.Group>
            <Form.Label>Data Final</Form.Label>
            <Form.Control
              type="date"
              value={filtros.data_fim || ''}
              onChange={e => handleInput('data_fim', e.target.value)}
              disabled={carregando}
            />
          </Form.Group>
        </Col>

        <Col xs={6} md={3}>
          <Form.Group>
            <Form.Label>Valor Inicial</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              value={filtros.val_ini}
              onChange={e => handleInput('val_ini', e.target.value)}
              disabled={carregando}
              placeholder="0,00"
            />
          </Form.Group>
        </Col>

        <Col xs={6} md={3}>
          <Form.Group>
            <Form.Label>Valor Final</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              value={filtros.val_fim}
              onChange={e => handleInput('val_fim', e.target.value)}
              disabled={carregando}
              placeholder="0,00"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="align-items-end mt-2">
        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label>Observação (contém)</Form.Label>
            <Form.Control
              type="text"
              value={filtros.observacao || ''}
              onChange={e => handleInput('observacao', e.target.value)}
              disabled={carregando}
              maxLength={200}
              placeholder="Texto a procurar nas observações"
            />
          </Form.Group>
        </Col>

        <Col xs={12} md="auto">
          <Form.Group>
            <Form.Label className="d-none d-md-block">&nbsp;</Form.Label>
            <Form.Check
              type="checkbox"
              id="rel-negocios-mostrar-hora"
              label="Mostrar hora"
              checked={!!filtros.mostrar_hora}
              onChange={e => handleInput('mostrar_hora', e.target.checked)}
              disabled={carregando}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md="auto" className="ml-md-auto">
          <Form.Group>
            <Form.Label className="d-none d-md-block">&nbsp;</Form.Label>
            <Button variant="primary" onClick={onGerar} disabled={carregando}>
              <FaSearch className="mr-1" />
              {carregando ? 'Gerando...' : 'Gerar'}
            </Button>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

export default FormFiltros;
